import { CONFERENCE_FAILED, CONFERENCE_JOINED, CONFERENCE_LEFT } from '../base/conference/actionTypes';
import { CONNECTION_ESTABLISHED, CONNECTION_FAILED } from '../base/connection/actionTypes';
import { hideDialog } from '../base/dialog/actions';
import { isDialogOpen } from '../base/dialog/functions';
import { JitsiConferenceErrors, JitsiConnectionErrors } from '../base/lib-jitsi-meet';
import { MEDIA_TYPE } from '../base/media/constants';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { isLocalTrackMuted } from '../base/tracks/functions.any';
import { parseURIString } from '../base/util/uri';
import { openLogoutDialog } from '../settings/actions';
import { CANCEL_LOGIN, LOGIN, LOGOUT, STOP_WAIT_FOR_OWNER, UPGRADE_ROLE_FINISHED, WAIT_FOR_OWNER } from './actionTypes';
import { closeLoginPopup, disableModeratorLogin, enableModeratorLogin, hideLoginDialog, openLoginDialog, openTokenAuthUrl, openWaitForOwnerDialog, redirectToDefaultLocation, stopWaitForOwner, waitForOwner } from './actions';
import { LoginDialog, WaitForOwnerDialog } from './components';
import { getTokenAuthUrl, isTokenAuthEnabled } from './functions';
import logger from './logger';
/**
 * Middleware that captures connection or conference failed errors and controls
 * moderator login availability and {@link LoginDialog}.
 *
 * FIXME Some of the complexity was introduced by the lack of dialog stacking.
 *
 * @param {Store} store - Redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    switch (action.type) {
        case CANCEL_LOGIN: {
            const { dispatch, getState } = store;
            const state = getState();
            const { thenableWithCancel } = state['features/authentication'];
            thenableWithCancel?.cancel();
            // The LoginDialog can be opened on top of "wait for owner". The app
            // should navigate only if LoginDialog was open without the
            // WaitForOwnerDialog.
            if (!isDialogOpen(store, WaitForOwnerDialog)) {
                if (_isWaitingForOwner(store)) {
                    // Instead of hiding show the new one.
                    const result = next(action);
                    dispatch(openWaitForOwnerDialog());
                    return result;
                }
                dispatch(hideLoginDialog());
                const { authRequired, conference } = state['features/base/conference'];
                const { passwordRequired } = state['features/base/connection'];
                // Only end the meeting if we are not already inside and trying to upgrade.
                // NOTE: Despite it's confusing name, `passwordRequired` implies an XMPP
                // connection auth error.
                if ((passwordRequired || authRequired) && !conference) {
                    dispatch(redirectToDefaultLocation());
                }
            }
            break;
        }
        case CONFERENCE_FAILED: {
            const { error } = action;
            // XXX The feature authentication affords recovery from
            // CONFERENCE_FAILED caused by
            // JitsiConferenceErrors.AUTHENTICATION_REQUIRED.
            let recoverable;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const [_lobbyJid, lobbyWaitingForHost] = error.params;
            if (error.name === JitsiConferenceErrors.AUTHENTICATION_REQUIRED
                || (error.name === JitsiConferenceErrors.MEMBERS_ONLY_ERROR && lobbyWaitingForHost)) {
                if (typeof error.recoverable === 'undefined') {
                    error.recoverable = true;
                }
                recoverable = error.recoverable;
            }
            if (error.name === JitsiConferenceErrors.MEMBERS_ONLY_ERROR && lobbyWaitingForHost) {
                if (recoverable) {
                    store.dispatch(enableModeratorLogin());
                }
                else {
                    store.dispatch(disableModeratorLogin());
                }
            }
            else if (error.name === JitsiConferenceErrors.AUTHENTICATION_REQUIRED) {
                if (recoverable) {
                    store.dispatch(waitForOwner());
                }
                else {
                    store.dispatch(stopWaitForOwner());
                }
            }
            break;
        }
        case CONFERENCE_JOINED: {
            const { dispatch } = store;
            if (_isWaitingForModerator(store)) {
                dispatch(disableModeratorLogin());
            }
            if (_isWaitingForOwner(store)) {
                dispatch(stopWaitForOwner());
            }
            dispatch(hideLoginDialog());
            // We may have been admitted to the conference while an inline token
            // auth popup was still open (e.g. an authenticated participant joined
            // and the backend let us in automatically). Dismiss the popup so it
            // doesn't linger on top of the conference.
            closeLoginPopup();
            break;
        }
        case CONFERENCE_LEFT:
            store.dispatch(disableModeratorLogin());
            store.dispatch(stopWaitForOwner());
            break;
        case CONNECTION_ESTABLISHED:
            store.dispatch(hideLoginDialog());
            break;
        case CONNECTION_FAILED: {
            const { error } = action;
            const { getState } = store;
            const state = getState();
            const { jwt } = state['features/base/jwt'];
            if (error
                && error.name === JitsiConnectionErrors.PASSWORD_REQUIRED
                && typeof error.recoverable === 'undefined'
                && !jwt) {
                error.recoverable = true;
                _handleLogin(store);
            }
            break;
        }
        case LOGIN: {
            _handleLogin(store);
            break;
        }
        case LOGOUT: {
            _handleLogout(store);
            break;
        }
        case STOP_WAIT_FOR_OWNER:
            _clearExistingWaitForOwnerTimeout(store);
            store.dispatch(hideDialog('WaitForOwnerDialog', WaitForOwnerDialog));
            break;
        case UPGRADE_ROLE_FINISHED: {
            const { error, progress } = action;
            if (!error && progress === 1) {
                store.dispatch(hideLoginDialog());
            }
            break;
        }
        case WAIT_FOR_OWNER: {
            _clearExistingWaitForOwnerTimeout(store);
            const { handler, timeoutMs } = action;
            action.waitForOwnerTimeoutID = setTimeout(handler, timeoutMs);
            // The WAIT_FOR_OWNER action is cyclic, and we don't want to hide the
            // login dialog every few seconds.
            isDialogOpen(store, LoginDialog)
                || store.dispatch(openWaitForOwnerDialog());
            break;
        }
    }
    return next(action);
});
/**
 * Will clear the wait for conference owner timeout handler if any is currently
 * set.
 *
 * @param {Object} store - The redux store.
 * @returns {void}
 */
function _clearExistingWaitForOwnerTimeout({ getState }) {
    const { waitForOwnerTimeoutID } = getState()['features/authentication'];
    waitForOwnerTimeoutID && clearTimeout(waitForOwnerTimeoutID);
}
/**
 * Checks if the cyclic "wait for conference owner" task is currently scheduled.
 *
 * @param {Object} store - The redux store.
 * @returns {boolean}
 */
function _isWaitingForOwner({ getState }) {
    return Boolean(getState()['features/authentication'].waitForOwnerTimeoutID);
}
/**
 * Checks if the cyclic "wait for moderator" task is currently scheduled.
 *
 * @param {Object} store - The redux store.
 * @returns {boolean}
 */
function _isWaitingForModerator({ getState }) {
    return getState()['features/authentication'].showModeratorLogin;
}
/**
 * Handles login challenge. Opens login dialog or redirects to token auth URL.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @returns {void}
 */
function _handleLogin({ dispatch, getState }) {
    const state = getState();
    const config = state['features/base/config'];
    const room = state['features/base/conference'].room;
    const { locationURL = { href: '' } } = state['features/base/connection'];
    const { tenant } = parseURIString(locationURL.href) || {};
    const { enabled: lowBandwidthModeEnabled } = state['features/base/low-bandwidth-mode'];
    const audioMuted = isLocalTrackMuted(state['features/base/tracks'], MEDIA_TYPE.AUDIO);
    const videoMuted = isLocalTrackMuted(state['features/base/tracks'], MEDIA_TYPE.VIDEO);
    const refreshToken = state['features/base/jwt'].refreshToken;
    if (!room) {
        logger.warn('Cannot handle login, room is undefined!');
        return;
    }
    if (!isTokenAuthEnabled(state)) {
        dispatch(openLoginDialog());
        return;
    }
    getTokenAuthUrl(config, locationURL, {
        audioMuted,
        lowBandwidthModeEnabled,
        skipPrejoin: true,
        videoMuted
    }, room, tenant, refreshToken)
        .then((tokenAuthServiceUrl) => {
        if (!tokenAuthServiceUrl) {
            logger.warn('Cannot handle login, token service URL is not set');
            return;
        }
        // 网页开发端：强制跳当前源门户，避免 Docker TOKEN_AUTH_URL 指到 :8443
        let redirectUrl = tokenAuthServiceUrl;
        try {
            const parsed = new URL(tokenAuthServiceUrl);
            const current = new URL(window.location.origin);
            if (parsed.host !== current.host) {
                const forced = new URL(window.location.origin);
                forced.searchParams.set('portalAuth', '1');
                forced.searchParams.set('room', room);
                if (parsed.searchParams.get('state')) {
                    forced.searchParams.set('state', parsed.searchParams.get('state') || '');
                }
                redirectUrl = forced.toString();
                logger.info(`tokenAuthUrl 主机与当前页不一致，改写为当前源 | from=${tokenAuthServiceUrl} to=${redirectUrl}`);
            }
        }
        catch (e) {
            logger.warn(`解析 tokenAuthUrl 失败，沿用原值 | ${e}`);
        }
        return dispatch(openTokenAuthUrl(redirectUrl));
    });
}
/**
 * Handles logout challenge. Opens logout dialog and hangs up the conference.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {string} logoutUrl - The url for logging out.
 * @returns {void}
 */
function _handleLogout({ dispatch, getState }) {
    const state = getState();
    const { conference } = state['features/base/conference'];
    if (!conference) {
        return;
    }
    dispatch(openLogoutDialog());
}
